<script setup lang="ts">
import { ref } from 'vue';
/**
 * The mark beside a money figure: a gold coin for credits, a cut gem for
 * diamonds.
 *
 * ## Drawn, not fetched — with one deliberate exception
 *
 * **The diamond is authored art** (`/assets/ui/diamond.webp`), by request: a
 * rendered gem with real refraction says "premium currency" in a way six flat
 * facets cannot, and that is the whole job of this particular mark. The rule below
 * is not thereby broken, because it is about rasters sized *to* the display: this
 * one is 160 px against a largest realistic use of ~18 CSS px at DPR 3 (≈55 px),
 * so it is downsampled at every size the app uses it at, which is sharp rather
 * than soft. It cost 8 KB. Judged on a proof sheet at 11–48 px over dark, over the
 * accent plate and over white, which is where a cutout's fringe shows.
 *
 * The drawn facets are kept as the fallback rather than deleted — see `gemFailed`.
 *
 * ## The coin is still drawn
 *
 * Same rule as the radar bezel, the title badges, the tutorial's key caps and
 * the daily-quest glyphs: this game renders at `devicePixelRatio`, a currency
 * mark sits inline at **0.7–1 rem**, and any fixed raster is soft on half the
 * machines that run it. At 14 device pixels there is no resolution to spare, so
 * the art has to be resolution-independent — and it also has to be *legible at
 * that size*, which is why neither of these carries internal detail: the coin is
 * a disc, a rim and one highlight, and the gem is six flat facets. Anything
 * finer becomes a coloured smudge, which is the failure the research icons'
 * own note records ("a subject with no mass mushes at 80 px") one order of
 * magnitude further down.
 *
 * ## It replaced a glyph, and that is the point
 *
 * Credits read `1,234 cr` and diamonds read `380 💎`. The first is an
 * abbreviation a new player has to learn, and the second is an **emoji** — so
 * what a diamond price actually looked like was whatever font the operating
 * system chose, which is a different shape, weight and hue on every platform and
 * is a colour this palette never picked. A drawn mark is the same on all of them.
 *
 * ## Sized in `em`, deliberately
 *
 * One component for the title bar's 1 rem readout, a card's 0.72 rem price and
 * the confirmation dialog's 2 rem headline. It takes its size from the text it
 * sits in, so a caller never states one, and the mark cannot fall out of step
 * with a font-size somebody tunes later. `vertical-align` puts it on the
 * baseline rather than the text's centre, which is where a coin looks dropped.
 *
 * ## The colours are fixed, not inherited
 *
 * A coin is gold and a diamond is not, whatever colour the number beside it is —
 * and the numbers *do* differ: credits are the HUD accent, diamonds are the
 * shop's pale blue, and both go grey while the wallet is still loading. So this
 * does not use `currentColor`; it dims with the text instead, through an
 * `opacity` a parent can set (`.pending` does), which keeps one mark rather than
 * a palette per state.
 */
const props = withDefaults(
  defineProps<{
    kind: 'credits' | 'diamonds';
    /**
     * Whether the mark is announced.
     *
     * `true` by default, because this **replaced the written unit**: with `cr`
     * and `💎` gone from the markup, the icon is the only thing left saying
     * which currency a figure is in, and a screen reader would otherwise read
     * "1,234" with no unit at all. Pass `false` only where the surrounding text
     * already names the currency in words, so it is not said twice.
     */
    labelled?: boolean;
  }>(),
  { labelled: true },
);

const label = () => (props.kind === 'credits' ? 'credits' : 'diamonds');

/**
 * The authored gem, and whether it arrived.
 *
 * `false` falls back to the drawn facets below — the same honest degradation a
 * livery pattern and a research icon make. A currency mark is not optional: a
 * figure with no unit beside it is worse than a figure with a simpler one.
 */
const gemFailed = ref(false);
</script>

<template>
  <svg
    v-if="kind === 'credits'"
    class="coin"
    viewBox="0 0 16 16"
    :role="labelled ? 'img' : undefined"
    :aria-label="labelled ? label() : undefined"
    :aria-hidden="labelled ? undefined : 'true'"
    focusable="false"
  >
    <defs>
      <linearGradient :id="`coin-face-${kind}`" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#F7D67A" />
        <stop offset="0.55" stop-color="#E2A93C" />
        <stop offset="1" stop-color="#B87A1E" />
      </linearGradient>
    </defs>
    <circle cx="8" cy="8" r="7" :fill="`url(#coin-face-${kind})`" />
    <circle cx="8" cy="8" r="7" fill="none" stroke="#8A5612" stroke-width="1" />
    <circle cx="8" cy="8" r="4.5" fill="none" stroke="#8A5612" stroke-width="0.9" opacity="0.55" />
    <path
      d="M3.6 5.4A5.6 5.6 0 0 1 8 2.9"
      fill="none"
      stroke="#FFF3CE"
      stroke-width="1.1"
      stroke-linecap="round"
      opacity="0.85"
    />
  </svg>

  <span v-else-if="!gemFailed" class="gem-art">
    <img
      src="/assets/ui/diamond.webp"
      :alt="labelled ? label() : ''"
      :aria-hidden="labelled ? undefined : 'true'"
      decoding="async"
      @error="gemFailed = true"
    />
  </span>

  <svg
    v-else
    class="gem"
    viewBox="0 0 16 16"
    :role="labelled ? 'img' : undefined"
    :aria-label="labelled ? label() : undefined"
    :aria-hidden="labelled ? undefined : 'true'"
    focusable="false"
  >
    <path d="M5 3.9H11L15 7.7L8 13.9L1 7.7Z" fill="#4E97BF" />
    <path d="M5 3.9H11L9.5 7.7H6.5Z" fill="#DCF2FF" />
    <path d="M5 3.9L1 7.7H6.5Z" fill="#A6DBF7" />
    <path d="M11 3.9L15 7.7H9.5Z" fill="#A6DBF7" />
    <path d="M1 7.7H8V13.9Z" fill="#8ECBE8" />
    <path d="M8 7.7H15L8 13.9Z" fill="#4A90B5" />
    <path d="M6.5 7.7L8 13.9L9.5 7.7Z" fill="#B9E4FA" />
    <path d="M1 7.7H15" stroke="#2C6685" stroke-width="0.6" opacity="0.6" />
  </svg>
</template>

<style scoped>
/* The art and the drawn marks share one box, so a fallback cannot change layout. */
svg,
.gem-art {
  /*
   * `1em` square and baseline-shifted. The shift is measured rather than picked:
   * at -0.14em the disc's centre lines up with the x-height's middle, which is
   * where a mark beside a numeral looks seated instead of floating or dropped.
   */
  width: 1em;
  height: 1em;
  vertical-align: -0.14em;
  /* The containing block the wrapper comment argues for. */
  position: relative;
  /* A currency mark is never a pointer target — the control around it is. */
  flex: none;
  pointer-events: none;
}

.gem-art {
  display: inline-block;
}

/*
 * `contain`, stated rather than left to the default, for the same reason the
 * wrapper exists: a parent that sets `object-fit: cover` on its images would
 * otherwise crop the gem's point off inside this box.
 */
.gem-art img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
</style>
